import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fetchAdminAnalytics, fetchAdminMessages } from '../../lib/api';
import { LogOut, TrendingUp, Users, Mail, Eye, Globe, Home as HomeIcon } from 'lucide-react';

const Stat = ({ label, value, sub }) => (
  <div className="bg-white p-6 border border-neutral-200">
    <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500">{label}</p>
    <p className="mt-2 text-3xl font-light">{value}</p>
    {sub && <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-neutral-400">{sub}</p>}
  </div>
);

const exportCsv = (filename, rows) => {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');

  const logout = () => {
    localStorage.removeItem('imodeff_admin_token');
    nav('/admin/login', { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [a, m] = await Promise.all([fetchAdminAnalytics(), fetchAdminMessages()]);
        if (!cancelled) {
          setAnalytics(a);
          setMessages(m);
        }
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.removeItem('imodeff_admin_token');
          nav('/admin/login', { replace: true });
          return;
        }
        if (!cancelled) setError('Failed to load analytics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [nav]);

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center gap-4">
        <p className="text-sm text-[#e11f3a]">{error}</p>
        <button onClick={logout} className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 hover:text-black">Sign out</button>
      </div>
    );
  }

  const totals = analytics?.totals || {};
  const daily = analytics?.daily || [];
  const languages = analytics?.languages || [];
  const topProps = analytics?.top_properties || [];
  const maxDaily = Math.max(1, ...daily.map((d) => d.views));

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* HEADER */}
      <header className="bg-white border-b border-neutral-200 px-6 md:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[11px] tracking-[0.3em] uppercase text-neutral-500 hover:text-black">
            <HomeIcon className="w-4 h-4 inline mr-1" /> Imodeff
          </Link>
          <span className="text-neutral-300">/</span>
          <h1 className="text-[13px] tracking-[0.3em] uppercase font-medium">Admin dashboard</h1>
        </div>
        <button
          data-testid="admin-logout"
          onClick={logout}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-neutral-600 hover:text-[#e11f3a]"
        >
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </header>

      {/* TABS */}
      <div className="px-6 md:px-10 pt-6 flex gap-6 border-b border-neutral-200 bg-white">
        {['overview', 'properties', 'messages'].map((t) => (
          <button
            key={t}
            data-testid={`admin-tab-${t}`}
            onClick={() => setTab(t)}
            className={`pb-4 text-[11px] tracking-[0.3em] uppercase transition-colors border-b-2 -mb-px ${
              tab === t ? 'border-[#e11f3a] text-black' : 'border-transparent text-neutral-500 hover:text-black'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="px-6 md:px-10 py-10">
        {tab === 'overview' && (
          <>
            {/* STAT CARDS */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Views 24h" value={totals.views_24h ?? 0} sub={`${totals.unique_24h ?? 0} unique`} />
              <Stat label="Views 7d" value={totals.views_7d ?? 0} sub={`${totals.unique_7d ?? 0} unique`} />
              <Stat label="Views 30d" value={totals.views_30d ?? 0} sub={`${totals.unique_30d ?? 0} unique`} />
              <Stat label="Messages" value={totals.contacts_total ?? 0} sub={`+${totals.contacts_7d ?? 0} last 7d`} />
            </div>

            {/* DAILY CHART */}
            <section className="mt-10 bg-white border border-neutral-200 p-6">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-4 h-4 text-[#e11f3a]" />
                <p className="text-[11px] tracking-[0.3em] uppercase">Pageviews — last 30 days</p>
              </div>
              {daily.length === 0 ? (
                <p className="text-sm text-neutral-500">No data yet.</p>
              ) : (
                <div className="flex items-end gap-1 h-40">
                  {daily.map((d) => (
                    <div key={d.date} className="flex-1 flex flex-col items-center group">
                      <div
                        className="w-full bg-black group-hover:bg-[#e11f3a] transition-colors relative"
                        style={{ height: `${(d.views / maxDaily) * 100}%` }}
                        title={`${d.date}: ${d.views} views · ${d.uniques} unique`}
                      >
                        <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-neutral-500 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                          {d.views}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-2 flex justify-between text-[10px] tracking-[0.2em] uppercase text-neutral-400">
                <span>{daily[0]?.date}</span>
                <span>{daily[daily.length - 1]?.date}</span>
              </div>
            </section>

            {/* LANGUAGES */}
            <section className="mt-8 bg-white border border-neutral-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-4 h-4 text-[#e11f3a]" />
                <p className="text-[11px] tracking-[0.3em] uppercase">Language — last 30 days</p>
              </div>
              {languages.length === 0 ? (
                <p className="text-sm text-neutral-500">No data yet.</p>
              ) : (
                <div className="space-y-3">
                  {languages.map((l) => {
                    const total = languages.reduce((s, x) => s + x.views, 0);
                    const pct = total ? Math.round((l.views / total) * 100) : 0;
                    return (
                      <div key={l.language} className="flex items-center gap-4">
                        <span className="w-8 text-[11px] tracking-[0.2em] uppercase">{l.language}</span>
                        <div className="flex-1 h-2 bg-neutral-100 relative">
                          <div className="absolute inset-y-0 left-0 bg-[#e11f3a]" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-16 text-right text-[11px] tracking-[0.2em] uppercase text-neutral-500">{l.views} · {pct}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {tab === 'properties' && (
          <section className="bg-white border border-neutral-200 p-6" data-testid="admin-properties-panel">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-[#e11f3a]" />
                <p className="text-[11px] tracking-[0.3em] uppercase">Top properties — last 30 days</p>
              </div>
              <button
                onClick={() => exportCsv('property-views.csv', topProps)}
                className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 hover:text-[#e11f3a]"
              >
                Export CSV
              </button>
            </div>
            {topProps.length === 0 ? (
              <p className="text-sm text-neutral-500">No property views yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-neutral-200">
                    <th className="py-3 text-[10px] tracking-[0.3em] uppercase text-neutral-500">Property</th>
                    <th className="py-3 text-[10px] tracking-[0.3em] uppercase text-neutral-500 text-right">Views</th>
                    <th className="py-3 text-[10px] tracking-[0.3em] uppercase text-neutral-500 text-right">Unique</th>
                  </tr>
                </thead>
                <tbody>
                  {topProps.map((p) => (
                    <tr key={p.slug} className="border-b border-neutral-100">
                      <td className="py-3">
                        <Link to={`/project/${p.slug}`} className="flex items-center gap-3 hover:text-[#e11f3a]">
                          {p.hero && <img src={p.hero} alt="" className="w-12 h-9 object-cover" />}
                          <span className="tracking-[0.15em] uppercase text-[13px]">{p.title}</span>
                        </Link>
                      </td>
                      <td className="py-3 text-right tabular-nums">{p.views}</td>
                      <td className="py-3 text-right tabular-nums text-neutral-500">{p.uniques}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {tab === 'messages' && (
          <section className="bg-white border border-neutral-200 p-6" data-testid="admin-messages-panel">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#e11f3a]" />
                <p className="text-[11px] tracking-[0.3em] uppercase">Contact messages ({messages.length})</p>
              </div>
              <button
                onClick={() => exportCsv('messages.csv', messages)}
                className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 hover:text-[#e11f3a]"
              >
                Export CSV
              </button>
            </div>
            {messages.length === 0 ? (
              <p className="text-sm text-neutral-500">No messages yet.</p>
            ) : (
              <div className="space-y-6">
                {messages.map((m) => (
                  <article key={m.id} className="border-b border-neutral-100 pb-6 last:border-0">
                    <div className="flex flex-wrap items-baseline gap-4 justify-between">
                      <div>
                        <p className="text-[13px] tracking-[0.15em] uppercase">{m.first_name} {m.last_name}</p>
                        <p className="text-[11px] text-neutral-500 mt-1">
                          <a href={`mailto:${m.email}`} className="hover:text-[#e11f3a]">{m.email}</a>
                          {m.phone ? <> · {m.phone}</> : null}
                        </p>
                      </div>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-neutral-400">
                        {new Date(m.created_at).toLocaleString()}
                      </p>
                    </div>
                    <p className="mt-3 text-sm text-neutral-700 whitespace-pre-line">{m.message}</p>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
